import { Contact } from '../models/contact.model';
import { Customer } from '../models/customer.model';
import { CustomerAddress } from '../models/customer-address.model';
import { PlasticsOrder } from '../models/plastics-order.model';
import { PlasticsOrderedProduct, PlasticsOrderedProductAdditionalCost } from '../models/plastics-ordered-product.model';
import { PlasticsOrderReviewResearch } from '../models/plastics-order-review-research.model';
import { ProductVersion } from '../models/product-version.model';
import { formatAddressLines } from './format-address-lines.function';
import { latestOrderReviewPriceSelection } from './plastics-order-acknowledgement.function';

/** Acknowledgements are USD. */
export const ORDER_ACKNOWLEDGEMENT_CURRENCY = 'USD';

/** One ordered product and the additional costs stored on it. */
export interface OrderAcknowledgementLine {
    partNumber: string;
    customerPartNumber: string;
    description: string;
    quantity: number;
    /** Reviewer-selected base unit price; the PO price is the default. */
    rate: number | null;
    /** Extended product amount (qty × rate). Null when the unit price is unknown. */
    amount: number | null;
    additionalCosts: PlasticsOrderedProductAdditionalCost[];
    dueDateFormatted: string;
    requestedDateFormatted: string;
}

export interface OrderAcknowledgementCustomer {
    id: string;
    name: string;
    /** PO-sender email from research. '' when research did not save one. Never a contact fallback. */
    email: string;
    billingName: string;
    billingAddress: string;
    shippingName: string;
    shippingAddress: string;
}

/** View data built from the order and its current lines for PDF generation. */
export interface OrderAcknowledgementData {
    orderNumber: string;
    purchaseOrderNumber: string;
    salesOrderNumbers: string;
    orderDateFormatted: string;
    dueDateFormatted: string;
    acknowledgedOnFormatted: string;
    customer: OrderAcknowledgementCustomer;
    dateChanges: string[];
    lines: OrderAcknowledgementLine[];
    /** Product amounts plus each line's additional costs. */
    total: number;
    totalFormatted: string;
    totalUnits: number;
}

export interface OrderAcknowledgementContext {
    order: PlasticsOrder;
    customer: Customer | null;
    lines: PlasticsOrderedProduct[];
    versionsByProductId: Map<string, ProductVersion>;
    researchByLineId: Map<string, PlasticsOrderReviewResearch>;
    addresses: CustomerAddress[];
    contacts: Contact[];
    /** Printed "Acknowledged" date. Defaults to now so production and tests can pin it. */
    acknowledgedOn?: Date;
}

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: ORDER_ACKNOWLEDGEMENT_CURRENCY });

export function formatOrderAcknowledgementMoney(value: number | null | undefined): string {
    return value === null || value === undefined ? '—' : money.format(value);
}

function formatDay(value: Date | string | null | undefined): string {
    if (!value) return '';
    const date = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
        ? new Date(`${value}T12:00:00`)
        : new Date(value);
    return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-US');
}

/** Human-readable gap between the PO date and the acknowledged due date. */
function describeShift(requested: string, due: Date | string): string {
    const from = new Date(`${requested}T12:00:00`);
    const to = typeof due === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(due)
        ? new Date(`${due}T12:00:00`)
        : new Date(due);
    const days = Math.round((to.getTime() - from.getTime()) / 86400000);
    if (!Number.isFinite(days) || days === 0) return '';
    const n = Math.abs(days);
    const span = n % 7 === 0 ? `${n / 7} week${n === 7 ? '' : 's'}` : `${n} day${n === 1 ? '' : 's'}`;
    return `${span} ${days > 0 ? 'later' : 'earlier'}`;
}

function cents(value: number): number {
    return Math.round(value * 100) / 100;
}

/** The same price and charge plan feeds the review preview, Finalize, and saved PDF. */
export function acknowledgementPricingForLine(
    line: PlasticsOrderedProduct,
    research: PlasticsOrderReviewResearch | null | undefined,
): { rate: number | null; amount: number | null; additionalCosts: PlasticsOrderedProductAdditionalCost[] } {
    const selection = latestOrderReviewPriceSelection(line);
    if (selection) {
        if (selection.researchId !== research?.id) throw new Error('ACK price selection is for older research. Choose the price again.');
        if (!Number.isFinite(selection.baseUnitPrice) || selection.baseUnitPrice < 0
            || !Number.isFinite(selection.surchargeUnitPrice) || selection.surchargeUnitPrice < 0
            || (selection.baseSource === 'customer-file' && selection.surchargeUnitPrice)) {
            throw new Error('ACK price selection is invalid. Choose the price again.');
        }
        const additionalCosts = (line.additionalCosts ?? []).filter(cost => !/material surcharge/i.test(cost.label));
        if (selection.surchargeUnitPrice) additionalCosts.push({
            label: 'Material Surcharge',
            amount: cents(selection.surchargeUnitPrice * line.quantity),
            createdOn: line.updatedOn,
        });
        return {
            rate: cents(selection.baseUnitPrice),
            amount: cents(selection.baseUnitPrice * line.quantity),
            additionalCosts,
        };
    }
    // Historical saved material costs are a split of the PO amount, not an
    // automatic extra charge. An explicit new selection overrides this path.
    const poRate = research?.result.pricing?.poUnitPrice ?? null;
    const additionalCosts = line.additionalCosts ?? [];
    const includedSurcharge = additionalCosts.filter(cost => /material surcharge/i.test(cost.label))
        .reduce((sum, cost) => sum + cost.amount, 0);
    const rate = poRate === null ? null : cents(poRate - includedSurcharge / line.quantity);
    if (rate !== null && rate < 0) throw new Error('Material surcharge exceeds the PO line total.');
    const amount = rate === null ? null : cents(poRate! * line.quantity - includedSurcharge);
    if (includedSurcharge && rate !== null && cents(rate * line.quantity) !== amount) {
        throw new Error('Material surcharge cannot be split evenly across this PO line.');
    }
    return { rate, amount, additionalCosts };
}

/** Address block: receiver (or the customer) on the first line, then the street lines. */
function addressBlock(address: CustomerAddress | undefined, contact: Contact | undefined, fallbackName: string): { name: string; lines: string } {
    if (!address) return { name: fallbackName, lines: '' };
    const name = (address.receiverName || '').trim() || fallbackName;
    const lines = [
        formatAddressLines(address.address, undefined, false),
        contact?.name ? `Attn: ${contact.name}` : '',
    ].filter(Boolean).join('\n');
    return { name, lines };
}

export function orderAcknowledgementFileName(order: Pick<PlasticsOrder, 'id' | 'orderNumber'>): string {
    return `Order_Acknowledgement_${order.orderNumber || order.id}.pdf`;
}

export function orderAcknowledgementPurchaseOrderNumber(order: Pick<PlasticsOrder, 'customerPurchaseOrderNumber'>): string {
    return order.customerPurchaseOrderNumber || '';
}

export function orderAcknowledgementSalesOrderNumbers(lines: Array<Pick<PlasticsOrderedProduct, 'salesOrderNumber'>>): string {
    return [...new Set(lines.map(line => (line.salesOrderNumber || '').trim()).filter(Boolean))].join(', ');
}

/** The PO-sender email the research step saved. Blank when none of the lines recorded one. */
export function orderReviewSenderEmail(researchByLineId: Map<string, PlasticsOrderReviewResearch> | Iterable<PlasticsOrderReviewResearch>): string {
    const items = researchByLineId instanceof Map ? researchByLineId.values() : researchByLineId;
    for (const research of items) {
        const email = (research.result?.senderEmail || '').trim();
        if (email) return email;
    }
    return '';
}

export const ORDER_REVIEW_SENDER_NOT_CONTACT_MESSAGE = 'sender not a customer contact; create contact later';

export function isOrderReviewSenderCustomerContact(
    senderEmail: string,
    contacts: Array<Pick<Contact, 'primaryEmail' | 'emails'>>,
): boolean {
    const needle = senderEmail.trim().toLowerCase();
    if (!needle) return false;
    return contacts.some(contact => {
        if ((contact.primaryEmail || '').trim().toLowerCase() === needle) return true;
        return (contact.emails ?? []).some(email => email.trim().toLowerCase() === needle);
    });
}

export function orderReviewSenderNotCustomerContactMessage(result: PlasticsOrderReviewResearch['result'] | null | undefined): string | null {
    const email = (result?.senderEmail || '').trim();
    if (!email) return null;
    if (result?.senderIsCustomerContact === false) return ORDER_REVIEW_SENDER_NOT_CONTACT_MESSAGE;
    return null;
}

export function buildOrderAcknowledgementData(ctx: OrderAcknowledgementContext): OrderAcknowledgementData {
    const customerName = ctx.customer?.name ?? '';
    const addressById = new Map(ctx.addresses.map(address => [address.id, address]));
    const contactById = new Map(ctx.contacts.map(contact => [contact.id, contact]));

    // Bill To: the customer's default billing address, else any billing address, else the default address.
    const customerAddresses = ctx.addresses.filter(address => address.customerId === ctx.customer?.id && !address.isOneTimeUse);
    const billingAddress = customerAddresses.find(address => address.isBillingAddress && address.isDefault)
        ?? customerAddresses.find(address => address.isBillingAddress)
        ?? customerAddresses.find(address => address.isDefault);
    // Ship To: the address the PO named on the order, else the customer's default shipping address.
    const shippingAddress = (ctx.order.shippingAddressId ? addressById.get(ctx.order.shippingAddressId) : undefined)
        ?? customerAddresses.find(address => address.isShippingAddress && address.isDefault)
        ?? customerAddresses.find(address => address.isShippingAddress);
    const shippingContact = ctx.order.shippingContactId ? contactById.get(ctx.order.shippingContactId) : undefined;
    const billingContact = billingAddress?.contactId ? contactById.get(billingAddress.contactId) : undefined;

    const billing = addressBlock(billingAddress, billingContact, customerName);
    const shipping = addressBlock(shippingAddress, shippingContact, customerName);

    const lines: OrderAcknowledgementLine[] = [];
    let total = 0;
    let totalUnits = 0;
    const dateChanges: string[] = [];
    const sorted = [...ctx.lines].sort((a, b) => (a.salesOrderNumber || '').localeCompare(b.salesOrderNumber || ''));
    for (const line of sorted) {
        const version = ctx.versionsByProductId.get(line.productId);
        const researchDocument = ctx.researchByLineId.get(line.id);
        const research = researchDocument?.result;
        const { rate, amount, additionalCosts } = acknowledgementPricingForLine(line, researchDocument);
        const dueDateFormatted = formatDay(line.dueDate);
        const requested = research?.estimating?.poRequestedDate ?? null;
        const requestedDateFormatted = requested && formatDay(requested) !== dueDateFormatted ? formatDay(requested) : '';
        if (requested && requestedDateFormatted) {
            const shift = describeShift(requested, line.dueDate);
            const label = version?.customerPartNumber || version?.partNumber || '';
            dateChanges.push(`${label}: your PO requested ${requestedDateFormatted}; the acknowledged due date is ${dueDateFormatted}${shift ? `, ${shift}` : ''}.`);
        }
        if (amount !== null) total += amount;
        totalUnits += line.quantity;
        lines.push({
            partNumber: version?.partNumber ?? '',
            customerPartNumber: version?.customerPartNumber ?? '',
            description: version?.description ?? '',
            quantity: line.quantity,
            rate,
            amount,
            additionalCosts,
            dueDateFormatted,
            requestedDateFormatted,
        });
        total += additionalCosts.reduce((sum, cost) => sum + cost.amount, 0);
    }

    const dueDates = [...new Set(lines.map(line => line.dueDateFormatted).filter(Boolean))];
    const dueDateFormatted = dueDates.length === 1 ? dueDates[0] : dueDates.length ? 'See lines' : '';

    return {
        orderNumber: ctx.order.orderNumber ?? '',
        purchaseOrderNumber: orderAcknowledgementPurchaseOrderNumber(ctx.order),
        salesOrderNumbers: orderAcknowledgementSalesOrderNumbers(sorted),
        orderDateFormatted: formatDay(ctx.order.createdOn),
        dueDateFormatted,
        acknowledgedOnFormatted: formatDay(ctx.acknowledgedOn ?? new Date()),
        customer: {
            id: ctx.customer?.id ?? '',
            name: customerName,
            email: orderReviewSenderEmail(ctx.researchByLineId),
            billingName: billing.name,
            billingAddress: billing.lines,
            shippingName: shipping.name,
            shippingAddress: shipping.lines,
        },
        dateChanges,
        lines,
        total: cents(total),
        totalFormatted: money.format(cents(total)),
        totalUnits,
    };
}
